/**
 * The wire shapes `Rws.AiInsights.Host.Requests.RequestEndpoints` returns for the approver's queue
 * (phase 17, `AIA-81`): the pending list, decide (approve/reject) and reassign. Gated on
 * `request.approve`. Raising a request on someone's behalf (`entitlement.administer`) is not built
 * here — it belongs to a request-raising screen, out of this phase's "Do" list.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** One request, as the approver's queue lists it. */
export interface RequestSummary {
  id: string;
  personId: string;
  requestType: string;
  payloadJson: string;
  status: string;
  createdAt: string;
  decidedAt: string | null;
  /** Who decided the request, or carried out its manual task, once one has. */
  decidedByName?: string | null;
  decidedByEmail?: string | null;
  /** For a provider-allocation request, each provider it lists with its before and after setting; otherwise null. */
  providerChanges?: ProviderChange[] | null;
  personDisplayName?: string | null;
  personEmail?: string | null;
  assignedToName?: string | null;
  assignedToEmail?: string | null;
  /** Display name of each provider, keyed by vendor id; present on provider-allocation requests. */
  providerNames?: Record<string, string> | null;
  currentAllocation?: { vendorId: string; enabled: boolean; amountUsd: number }[] | null;
}

const pendingRequestsPath = "/api/entitlement/requests";

function decisionPath(id: string): string {
  return `${pendingRequestsPath}/${encodeURIComponent(id)}/decision`;
}

function reassignmentPath(id: string): string {
  return `${pendingRequestsPath}/${encodeURIComponent(id)}/reassignment`;
}

/**
 * Calls `GET /api/entitlement/requests`. With `includeCompleted`, the response also carries every
 * request that has already been decided or withdrawn (newest first, after the pending ones). With `assignedToMe`, only the requests
 * currently assigned to the signed-in person are returned, even for a holder of `request.approve.any`.
 */
export async function fetchPendingRequests(includeCompleted = false, assignedToMe = false): Promise<AdminResult<RequestSummary[]>> {
  const query = [includeCompleted ? "includeCompleted=true" : "", assignedToMe ? "assignedToMe=true" : ""].filter((part) => part !== "").join("&");
  const path = query === "" ? pendingRequestsPath : `${pendingRequestsPath}?${query}`;
  return adminRequest<RequestSummary[]>(path, { method: "GET" }, "pending requests");
}

/**
 * Calls `POST /api/entitlement/requests/{id}/decision`. `amendedAccessGroups` (vendor id to group ids) replaces the groups an
 * approver approves for a provider the request switches on (AIA-109); it is left out of the body when absent.
 */
export async function decideRequest(
  id: string,
  approve: boolean,
  notes: string | null,
  amendedAccessGroups?: Record<string, string[]>,
): Promise<AdminResult<{ requestId: string; decisionId: string; status: string }>> {
  return adminRequest(decisionPath(id), { method: "POST", body: JSON.stringify({ approve, notes, ...(amendedAccessGroups ? { amendedAccessGroups } : {}) }) }, "decision");
}

/** Calls `POST /api/entitlement/requests/{id}/reassignment`. */
export async function reassignRequest(id: string, newApproverPersonId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(reassignmentPath(id), { method: "POST", body: JSON.stringify({ newApproverPersonId }) }, "reassignment");
}

/** Calls `POST /api/entitlement/manual-tasks/{id}/complete`, recording a manual vendor task as carried out by hand (`CON-34`). */
export async function completeManualTask(id: string, notes: string | null): Promise<AdminResult<null>> {
  return adminRequest<null>(`/api/entitlement/manual-tasks/${encodeURIComponent(id)}/complete`, { method: "POST", body: JSON.stringify({ notes }) }, "manual task");
}

/**
 * One provider a provider-allocation request lists. `beforeEnabled` and `beforeAmountUsd` are null when the request
 * never recorded the setting it replaced (it was denied or withdrawn).
 */
export interface ProviderChange {
  vendorId: string;
  beforeEnabled: boolean | null;
  beforeAmountUsd: number | null;
  afterEnabled: boolean;
  afterAmountUsd: number;
  /**
   * The vendor access groups the person had on the provider when the request was raised (AIA-109). Null or absent when none
   * were recorded: the provider has no groups, or the request predates recording them.
   */
  beforeAccessGroups?: string[] | null;
}

/** One current or previous request for a person, with its outcome and who decided it. */
export interface PersonRequestHistoryItem {
  id: string;
  requestType: string;
  payloadJson: string;
  status: string;
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decidedByEmail: string | null;
  decisionNotes: string | null;
  /** For a provider-allocation request, each provider it lists with its before and after setting; otherwise null. */
  providerChanges: ProviderChange[] | null;
  /** While the request waits, the person it is assigned to, so its requester can follow up; absent otherwise. */
  assignedToName?: string | null;
  assignedToEmail?: string | null;
}

/** Calls `GET /api/users/{personId}/requests` (gated on `entitlement.administer`). */
export async function fetchPersonRequests(personId: string): Promise<AdminResult<PersonRequestHistoryItem[]>> {
  return adminRequest<PersonRequestHistoryItem[]>(`/api/users/${encodeURIComponent(personId)}/requests`, { method: "GET" }, "request history");
}

/** A person who may approve requests, offered as a reassignment target. */
export interface Approver {
  personId: string;
  displayName: string;
  email: string;
}

/** Calls `GET /api/entitlement/requests/approvers`. */
export async function fetchApprovers(): Promise<AdminResult<Approver[]>> {
  return adminRequest<Approver[]>(`${pendingRequestsPath}/approvers`, { method: "GET" }, "approvers");
}
